/**
 * The commission workbench's own visibility gate.
 *
 * OWNER DECISION (2026-10-17): the module is visible only when the signed-in
 * user holds the commissions reviewer capability AND the
 * `commissions.workbench.enabled` feature is on. Both answers come from
 * GET /api/whoami. Missing, malformed, loading or errored → CLOSED.
 *
 * This deliberately differs from the rail (routing/nav-gating.ts), which fails
 * OPEN when roles are unknown and lets platform admins see every module. Here:
 *   - unknown roles   → closed
 *   - platform admin  → NOT a bypass; the reviewer role is required
 *   - flag not `true` → closed (no truthiness; the string "true" is closed)
 *
 * INPUTS. Roles come from whoami (experts.user_roles) and are interpreted by
 * the RBAC model (`hasReviewCapability`); the flag is whoami.features, read
 * with the same strictness as useFeatureFlag, which the rail uses — the spec
 * pins that the two agree. `gateInputsFromWhoami` is the only payload reader.
 *
 * NOT THE SECURITY BOUNDARY. This hides UI. The commission API must enforce
 * `review:commissions` server-side on every endpoint (docs/architecture/
 * commission-workbench-api.md); hiding the page is not authorization.
 */
import { useWhoami } from '@/auth/useWhoami';
import { deriveAuthorityKeys } from '@/features/admin/lib/rbac-model';

/** Stored role string that confers the capability (RBAC PR adds it to the model). */
export const COMMISSIONS_REVIEWER_ROLE = 'commissions:reviewer';
/** The authority name the server enforces (spec §4.5). */
export const REVIEW_COMMISSIONS = 'review:commissions';
/** The whoami feature key (flag PR). */
export const COMMISSIONS_WORKBENCH_FLAG = 'commissions.workbench.enabled';

export interface CommissionsGateInputs {
  /** Stored role strings, or undefined when they could not be read. */
  roles: readonly string[] | undefined;
  /** whoami.features, or undefined when the payload has none. */
  features: Readonly<Record<string, unknown>> | undefined;
}

export type CommissionsGateClosedReason =
  | 'loading'
  | 'identity-unavailable'
  | 'missing-capability'
  | 'flag-missing'
  | 'flag-off';

export type CommissionsGateDecision =
  | { open: true }
  | { open: false; reason: CommissionsGateClosedReason };

/**
 * Does this role set confer `review:commissions`?
 *
 * Read from the RBAC model (rbac spec 1.5.0): the authority is explicit-grant-
 * only, conferred by commissions:reviewer and deliberately NOT by the platform
 * admin role, so "admin is not a bypass" holds through the model itself.
 */
export function hasReviewCapability(
  roles: readonly string[] | undefined,
): boolean {
  if (!roles) return false;
  return deriveAuthorityKeys(roles).includes(REVIEW_COMMISSIONS);
}

export function decideCommissionsGate(
  inputs: CommissionsGateInputs,
): CommissionsGateDecision {
  if (!hasReviewCapability(inputs.roles)) {
    return { open: false, reason: 'missing-capability' };
  }
  if (!inputs.features || !(COMMISSIONS_WORKBENCH_FLAG in inputs.features)) {
    return { open: false, reason: 'flag-missing' };
  }
  if (inputs.features[COMMISSIONS_WORKBENCH_FLAG] !== true) {
    return { open: false, reason: 'flag-off' };
  }
  return { open: true };
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Read the gate's two inputs out of a whoami payload, defensively. */
export function gateInputsFromWhoami(payload: unknown): CommissionsGateInputs {
  if (!isPlainObject(payload)) return { roles: undefined, features: undefined };
  const rawRoles = payload.roles;
  const roles =
    Array.isArray(rawRoles) && rawRoles.every((r) => typeof r === 'string')
      ? (rawRoles as string[])
      : undefined;
  const features = isPlainObject(payload.features)
    ? payload.features
    : undefined;
  return { roles, features };
}

/** The gate, as the workbench and the pop-out viewer read it. */
export function useCommissionsGate(): CommissionsGateDecision {
  const whoami = useWhoami();
  // An errored query keeps its last good `data`, so check the error FIRST: a
  // failed refetch must close the gate rather than keep serving a role or flag
  // that may since have been revoked.
  if (whoami.isError) return { open: false, reason: 'identity-unavailable' };
  if (whoami.isSuccess) {
    return decideCommissionsGate(gateInputsFromWhoami(whoami.data));
  }
  return { open: false, reason: 'loading' };
}
